import React from "react";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";

export const ROLE_LABELS = {
  owner: "Propriétaire",
  manager: "Gestionnaire",
  foreman: "Chef de chantier",
  worker: "Ouvrier",
  // BTP-Suivi : 5e rôle pour l'Espace Sous-traitant (cf. PR #209-#214).
  // L'invitation passe par /admin/soustraitants (form dédié avec SIRET/
  // taux horaire/spécialités), pas par InviteUserDialog standard.
  soustraitant: "Sous-traitant",
};

// Concise role definitions surfaced via tooltip throughout the app.
export const ROLE_DESCRIPTIONS = {
  owner:
    "Accès total : facturation, réglages entreprise, gestion des chantiers, équipes, plannings et données RGPD.",
  manager:
    "Gère les chantiers et les équipes : créer/modifier des chantiers, inviter des utilisateurs, valider feuilles d'heures et absences.",
  foreman:
    "Supervise le terrain : modifie les chantiers dont il est responsable, valide les feuilles de son équipe, gère son planning.",
  worker:
    "Pointe ses heures, consulte son planning, demande ses absences. Lecture seule sur les chantiers où il est affecté.",
  soustraitant:
    "Entreprise / indépendant externe : voit ses chantiers affectés, ses tâches assignées, dépose ses factures PDF. Pour inviter un sous-traitant, utiliser la page /admin/soustraitants.",
};

const CLASSES = {
  owner: "bg-danger/10 text-danger border-danger/20",
  manager: "bg-brand/10 text-brand border-brand/20",
  foreman: "bg-warning/10 text-warning border-warning/30",
  worker: "bg-muted text-foreground border-border",
  soustraitant: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

/**
 * @param {string} role
 * @param {string=} className
 * @param {boolean=} withTooltip Wrap badge in a tooltip explaining the role.
 */
export default function RoleBadge({ role, className, withTooltip = false }) {
  const badge = (
    <Badge
      data-testid={`role-badge-${role}`}
      variant="outline"
      className={cn(
        "font-medium",
        CLASSES[role] || CLASSES.worker,
        withTooltip && "cursor-help",
        className,
      )}
    >
      {ROLE_LABELS[role] || role}
    </Badge>
  );
  if (!withTooltip) return badge;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-xs text-xs leading-relaxed"
          data-testid={`role-tooltip-${role}`}
        >
          <p className="font-semibold text-[13px] mb-1">
            {ROLE_LABELS[role] || role}
          </p>
          <p>{ROLE_DESCRIPTIONS[role] || ""}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
